/**
 * Audit Log PDF Export with MCA Compliance
 *
 * Exports audit trails to PDF format with hash verification,
 * compliance metadata, and regulatory references for MCA inspections.
 *
 * @module utils/pdf/auditExportPDF
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import {
  createPDF,
  addReportHeader,
  addComplianceMetadata,
  addHashVerificationBanner,
  addMCAFooter,
  PDF_CONFIG,
  getPageDimensions
} from './pdfCore';
import { verifyHashChain, sortEntriesByTimestamp } from '../hashUtils';
import { loadAuditLog } from '../auditStorage';

/**
 * Export audit log to PDF with MCA compliance metadata
 *
 * @param {string} vesselId - Vessel ID
 * @param {string} moduleName - Module name (equipment, drills, incidents, crew)
 * @param {Array} auditEntries - Array of audit log entries (optional, will load if not provided)
 * @param {Object} currentUser - Current user object
 * @param {Object} vessel - Vessel object with officialNumber, name
 * @returns {Promise<void>}
 */
export async function exportAuditLogToPDF(vesselId, moduleName, auditEntries = null, currentUser, vessel) {
  try {
    // Load audit entries if not provided.
    // FT-512: loadAuditLog signature is (module, vesselId) — the previous
    // call passed them reversed, which read the wrong (empty) storage key.
    if (!auditEntries) {
      auditEntries = await loadAuditLog(moduleName, vesselId);
    }

    if (!auditEntries || auditEntries.length === 0) {
      alert('No audit entries found to export.');
      return;
    }

    // Sort entries by timestamp (oldest first for correct chain order)
    const sortedEntries = sortEntriesByTimestamp(auditEntries);

    // Create PDF document in landscape mode for wider tables
    const pdf = new jsPDF('landscape', 'mm', 'a4');
    const { margin } = getPageDimensions(pdf);
    let yPos = margin;

    // Add report header
    const title = `${moduleName.charAt(0).toUpperCase() + moduleName.slice(1)} Audit Trail`;
    yPos = addReportHeader(pdf, yPos, {
      title,
      vesselName: vessel.name || 'Unknown Vessel',
      category: vessel.vesselType || 'Unknown Category',
      filters: {}
    });
    yPos += 3;

    // Add compliance metadata (Official Number, retention period)
    yPos = addComplianceMetadata(pdf, vessel, moduleName, yPos);
    yPos += 3;

    // Add hash verification banner
    yPos = await addHashVerificationBanner(pdf, sortedEntries, yPos);
    yPos += 5;

    // Prepare table data
    const tableData = sortedEntries.map(entry => {
      const timestamp = new Date(entry.timestamp).toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
      // FT-512: entries written by useAuditTrail.logChange use a NESTED shape
      // (entry.user.name / entry.item.name). Older/synthetic callers use a FLAT
      // shape (entry.userName / entry.itemName). Tolerate both so the User and
      // Item columns show real values instead of falling back to 'System'/'N/A'.
      const userName = entry.userName || entry.user?.name || entry.userId || entry.user?.id || 'System';
      const action = entry.action || 'Update';
      const itemName = entry.itemName || entry.item?.name || entry.itemId || entry.item?.id || 'N/A';

      // Determine hash status
      let hashStatus = 'Legacy (No Hash)';
      if (entry.hash) {
        hashStatus = entry.previousHash ? 'Chained' : 'Genesis';
      }

      return [timestamp, userName, action, itemName, hashStatus];
    });

    // Add table with audit entries
    autoTable(pdf, {
      startY: yPos,
      head: [['Timestamp', 'User', 'Action', 'Item', 'Hash Status']],
      body: tableData,
      theme: 'grid',
      styles: {
        fontSize: PDF_CONFIG.fonts.table.size,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [26, 84, 144], // PDF_CONFIG primary color
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: {
        left: margin,
        right: margin
      }
    });

    // Add footer to all pages
    const exportTimestamp = new Date().toISOString();
    const totalPages = pdf.internal.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      addMCAFooter(pdf, exportTimestamp, i, totalPages);
    }

    // Generate filename with vessel Official Number and timestamp
    const fileTimestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
    const officialNumber = vessel.officialNumber || vesselId;
    const filename = `audit_log_${moduleName}_${officialNumber}_${fileTimestamp}.pdf`;

    // Save PDF
    pdf.save(filename);

  } catch (error) {
    console.error('Error exporting audit log to PDF:', error);
    alert(`Failed to export audit log: ${error.message}`);
    throw error;
  }
}

/**
 * Export audit log with pagination for large datasets
 *
 * @param {string} vesselId - Vessel ID
 * @param {string} moduleName - Module name
 * @param {number} maxEntriesPerPage - Maximum entries per page (default: 500)
 * @param {Object} currentUser - Current user object
 * @param {Object} vessel - Vessel object
 * @returns {Promise<void>}
 */
export async function exportAuditLogPaginated(vesselId, moduleName, maxEntriesPerPage = 500, currentUser, vessel) {
  // FT-512: fix reversed args — must match the corrected loadAuditLog(module, vesselId) order.
  const allEntries = await loadAuditLog(moduleName, vesselId);

  if (!allEntries || allEntries.length === 0) {
    alert('No audit entries found to export.');
    return;
  }

  if (allEntries.length <= maxEntriesPerPage) {
    // Single PDF
    return exportAuditLogToPDF(vesselId, moduleName, allEntries, currentUser, vessel);
  }

  // Split into chunks and create multiple PDFs
  for (let i = 0; i < allEntries.length; i += maxEntriesPerPage) {
    const chunk = allEntries.slice(i, i + maxEntriesPerPage);
    const pageNum = Math.floor(i / maxEntriesPerPage) + 1;

    // Export this chunk
    await exportAuditLogToPDF(vesselId, moduleName, chunk, currentUser, vessel);

    // Brief pause between exports
    await new Promise(resolve => setTimeout(resolve, 100));
  }
}
